/**
 * Taxes are not always fixed. Instead, they are based on the taxable figure.
 * In Belgium, we don't have a flat percentage for the whole figure.
 * Instead, we have a system with brackets (also known as "schijven" in Dutch).
 * Each bracket can have its own fixed tax or percentage tax. For same brackets, both can even be 0. This is a so-called tax free bracket.
 * 
 * To obtain the tax for a figure, the figure has to be split into the brackets and the tax has to be calculated for each bracket.
 * Then the results have to be summed.
 * 
 * Brackets have a lower bound and an upper bound. To reduce the number of typos, we don't include the lower bound. 
 * We simply use the upper bound of the previous bracket.
 */
export class Bracket {
    constructor(
        public upperBound: number,
        public percentageTax: number,
        public fixedTax: number
    ) { }

    getTaxes(figure: number, lowerBound: number) {
        if (figure < lowerBound) {
            return 0
        }

        const actualUpperBound = figure > this.upperBound ? this.upperBound : figure
        return (actualUpperBound - lowerBound) * this.percentageTax / 100 + this.fixedTax
    }
}

export class Taxes {
    constructor(public socialContribution: number,
        public personalIncomeTax: number,
        public corporationAndWithholdingTax: number,
        public administration: number) { }

    total() {
        return this.socialContribution + this.personalIncomeTax + this.corporationAndWithholdingTax + this.administration
    }
}

export function calculateTaxes(figure: number, brackets: Array<Bracket>): number {
    if (figure < 0) {
        throw new Error("It's impossible to calculate taxes on a negative number.")
    }

    brackets.sort((left: Bracket, right: Bracket) => left.upperBound - right.upperBound)
    let lowerBound = 0
    let result = 0
    for (let bracket of brackets) {
        result += bracket.getTaxes(figure, lowerBound)
        lowerBound = bracket.upperBound
    }
    return result
}